import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import { 
  Lock, 
  Visibility, 
  VisibilityOff, 
  Check, 
  Close,
  Security,
  ArrowBack
} from '@mui/icons-material';
import ResetPwdService from '../service/ResetPwdService';

const PasswordReset = ({ onSuccess, onBack, resetData }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const passwordRequirements = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = password === confirmPassword;
  const metCount = Object.values(passwordRequirements).filter(Boolean).length;

  const getPasswordStrength = () => {
    if (!password) return { level: 0, text: '', color: 'grey' };
    if (metCount < 2) return { level: 25, text: 'Faible', color: 'error' };
    if (metCount < 4) return { level: 60, text: 'Moyen', color: 'warning' };
    return { level: 100, text: 'Fort', color: 'success' };
  };

  const strength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setAlert({ severity: 'error', message: 'Le mot de passe ne respecte pas tous les critères' });
      return;
    }

    if (!passwordsMatch) {
      setAlert({ severity: 'error', message: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      const data = await ResetPwdService.resetPassword({
        token: resetData.token,
        password: password,
        confirmPassword: confirmPassword
      });

      if (data.success) {
        setAlert({ severity: 'success', message: data.message });
        
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setAlert({ severity: 'error', message: data.message || 'Erreur lors de la réinitialisation' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setAlert({ severity: 'error', message: 'Erreur de connexion. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary.main">
          🔐 Nouveau mot de passe
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Étape 4/4 : Créez votre nouveau mot de passe
        </Typography>
      </Box>

      {/* Info utilisateur */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Typography variant="body2" align="center" color="primary.main">
          Création d'un nouveau mot de passe pour <strong>{resetData.userInfo?.email}</strong>
        </Typography>
      </Paper>

      {/* Alerte */}
      {alert && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Exigences du mot de passe */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color="primary" />
          Exigences du mot de passe
        </Typography>
        
        <List dense>
          {[
            { key: 'length', text: 'Au moins 8 caractères' },
            { key: 'upper', text: 'Au moins 1 majuscule (A-Z)' },
            { key: 'lower', text: 'Au moins 1 minuscule (a-z)' },
            { key: 'number', text: 'Au moins 1 chiffre (0-9)' }
          ].map(({ key, text }) => (
            <ListItem key={key} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {passwordRequirements[key] ? (
                  <Check color="success" fontSize="small" />
                ) : (
                  <Close color="error" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={text}
                primaryTypographyProps={{ 
                  variant: 'body2',
                  color: passwordRequirements[key] ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Formulaire */}
      <Paper 
        component="form" 
        onSubmit={handleSubmit}
        sx={{ 
          p: 3, 
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        {/* Champ mot de passe */}
        <TextField
          fullWidth
          label="Nouveau mot de passe"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Saisissez votre nouveau mot de passe"
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="primary" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Indicateur de force */}
        {password && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Force du mot de passe
              </Typography>
              <Typography variant="body2" color={`${strength.color}.main`}>
                {strength.text}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={strength.level}
              color={strength.color}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Champ confirmation */}
        <TextField
          fullWidth
          label="Confirmer le mot de passe"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirmez votre nouveau mot de passe"
          disabled={loading}
          error={confirmPassword && !passwordsMatch}
          helperText={confirmPassword && !passwordsMatch ? 'Les mots de passe ne correspondent pas' : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="primary" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 4 }}
        />

        {/* Bouton de soumission */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !isPasswordValid || !passwordsMatch}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Security />}
          sx={{
            py: 1.5,
            background: '#339669',
            '&:hover': {
              background: '#339669',
            },
            '&:disabled': {
              background: 'grey.400',
            }
          }}
        >
          {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
        </Button>
      </Paper>

      {/* Lien retour */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={onBack}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <ArrowBack fontSize="small" />
          Annuler et recommencer
        </Link>
      </Box>
    </Box>
  );
};

export default PasswordReset;